import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ShieldCheck, AlertTriangle, ArrowRight, Info } from 'lucide-react'
import { useOnboardingStore } from '@/stores/onboardingStore'

// Map products to their required frameworks
const PRODUCT_FRAMEWORK_MAP: Record<string, string[]> = {
  clinical: ['hipaa'],
  talent: ['eeoc'],
  legal: ['batson'],
  education: ['ferpa'],
  finance: ['finra'],
  engage: [],
  coach: [],
  identity: [],
}

export default function AttestationOverview() {
  const navigate = useNavigate()
  const { selectedProducts, setStep } = useOnboardingStore()

  // This should be step 5 (after products)
  useEffect(() => {
    setStep(5)
  }, [setStep])

  // Determine which frameworks are required based on selected products
  const requiredFrameworks = Array.from(
    new Set(
      selectedProducts.flatMap((productId) => PRODUCT_FRAMEWORK_MAP[productId] || [])
    )
  )

  const hasRegulatedProducts = requiredFrameworks.length > 0

  const handleContinue = () => {
    if (hasRegulatedProducts) {
      navigate('/onboarding/attestation/frameworks')
    } else {
      navigate('/onboarding/invite-team')
    }
  }

  const handleSkip = () => {
    navigate('/onboarding/invite-team')
  }

  return (
    <div>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-copper-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-copper-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-midnight-900">
            Regulatory Compliance
          </h1>
        </div>
        <p className="text-warm-600">
          Some products require attestation to regulatory compliance frameworks
        </p>
      </motion.div>

      {hasRegulatedProducts ? (
        <>
          {/* Alert Box */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800 font-heading font-semibold">
                Attestation Required
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Based on your product selections, you'll need to complete attestation
                for the following regulatory frameworks before using certain features.
              </p>
            </div>
          </motion.div>

          {/* Framework Overview */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <h2 className="text-lg font-heading font-semibold text-midnight-900 mb-4">
              Required Frameworks
            </h2>
            <div className="space-y-3">
              {requiredFrameworks.map((framework) => (
                <FrameworkOverviewCard key={framework} framework={framework} />
              ))}
            </div>
          </motion.div>

          {/* What is Attestation */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8 p-5 bg-midnight-50 rounded-xl"
          >
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-midnight-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-heading font-semibold text-midnight-900 mb-2">
                  What is Attestation?
                </h3>
                <ul className="text-sm text-midnight-700 space-y-2">
                  <li>
                    <strong>Compliance Commitment:</strong> An authorized representative
                    from your organization confirms compliance with regulatory requirements.
                  </li>
                  <li>
                    <strong>Legal Acknowledgment:</strong> You acknowledge understanding
                    of the framework's requirements and commit to proper usage.
                  </li>
                  <li>
                    <strong>Digital Signature:</strong> Your authorized representative
                    provides a digital signature for the attestation.
                  </li>
                  <li>
                    <strong>Annual Renewal:</strong> Attestations are valid for one year
                    and require renewal to maintain access to regulated features.
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <button
              onClick={handleContinue}
              className="flex-1 btn btn-primary flex items-center justify-center gap-2"
            >
              Continue to Framework Selection
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleSkip}
              className="btn btn-secondary"
            >
              Skip for Now
            </button>
          </motion.div>

          {/* Skip Note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-4 text-xs text-warm-500 text-center"
          >
            You can complete attestation later from your dashboard settings.
            Some features will be restricted until attestation is complete.
          </motion.p>
        </>
      ) : (
        <>
          {/* No Attestation Needed */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 p-6 bg-emerald-50 border border-emerald-200 rounded-xl text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-lg font-heading font-semibold text-emerald-800 mb-2">
              No Attestation Required
            </h2>
            <p className="text-sm text-emerald-700">
              Based on your product selections, no regulatory attestation is required
              at this time. You can proceed to the next step.
            </p>
          </motion.div>

          {/* Info Box */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8 p-4 bg-midnight-50 rounded-xl flex items-start gap-3"
          >
            <Info className="w-5 h-5 text-midnight-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-midnight-700">
              If you add regulated products in the future (Clinical, Legal, Talent,
              Education, or Finance), you'll be prompted to complete the appropriate
              attestation at that time.
            </p>
          </motion.div>

          {/* Continue Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <button
              onClick={handleContinue}
              className="w-full btn btn-primary flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </>
      )}
    </div>
  )
}

// Framework overview card component
function FrameworkOverviewCard({ framework }: { framework: string }) {
  const frameworkData: Record<string, { name: string; description: string; product: string }> = {
    hipaa: {
      name: 'HIPAA',
      description: 'Health Insurance Portability and Accountability Act',
      product: 'Clinical',
    },
    eeoc: {
      name: 'EEOC',
      description: 'Equal Employment Opportunity Commission',
      product: 'Talent',
    },
    batson: {
      name: 'Batson',
      description: 'Batson v. Kentucky (Jury Selection)',
      product: 'Legal',
    },
    ferpa: {
      name: 'FERPA',
      description: 'Family Educational Rights and Privacy Act',
      product: 'Education',
    },
    finra: {
      name: 'FINRA',
      description: 'Financial Industry Regulatory Authority',
      product: 'Finance',
    },
  }

  const data = frameworkData[framework]
  if (!data) return null

  return (
    <div className="p-4 bg-white border border-warm-200 rounded-lg">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold text-midnight-900">
            {data.name}
          </h3>
          <p className="text-sm text-warm-600">{data.description}</p>
        </div>
        <span className="px-2 py-1 bg-midnight-100 text-midnight-700 rounded text-xs font-heading">
          {data.product}
        </span>
      </div>
    </div>
  )
}
